import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  ChatResourceFooter,
  type ChatResourceKind,
} from "@/components/chat/ChatResourceFooter";
import {
  ConversationResourceInvokeType,
  invokeCreateConversationResource,
  notifyConversationResourcesChanged,
} from "@/services/conversationResourceService";

export type AIMentorPageActionsFooterProps = {
  sessionId?: string | null;
  hasTranscript: boolean;
  isTranscriptLoading?: boolean;
  /** Voice mode passes local transcript; text mode relies on persisted history on the server. */
  transcriptLines?: { role: "user" | "assistant"; content: string }[];
};

export const AIMentorPageActionsFooter = ({
  sessionId,
  hasTranscript,
  isTranscriptLoading = false,
  transcriptLines,
}: AIMentorPageActionsFooterProps) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState<ConversationResourceInvokeType | null>(null);

  const noSessionOrEmpty = !sessionId || (!hasTranscript && !transcriptLines?.length);

  const disabledReason =
    isTranscriptLoading
      ? "Loading conversation…"
      : noSessionOrEmpty
        ? "Start chatting to turn this conversation into a resource."
        : null;

  const run = async (resourceType: ConversationResourceInvokeType) => {
    if (!sessionId || busy) return;
    try {
      setBusy(resourceType);
      const result = await invokeCreateConversationResource({
        sessionId,
        resourceType,
        ...(transcriptLines?.length ? { transcriptLines } : {}),
      });
      notifyConversationResourcesChanged();
      const savedLabel = resourceType === "voice_note" ? "Voice note saved" : "Handout saved";
      const openLabel = resourceType === "voice_note" ? "Listen" : "Open";
      toast({
        title: savedLabel,
        description: `“${result.title}” is in My Resources → Personalized Resources.`,
        action: result.url ? (
          <ToastAction
            altText={`${openLabel} the resource you just created`}
            onClick={() => window.open(result.url!, "_blank", "noopener,noreferrer")}
          >
            {openLabel}
          </ToastAction>
        ) : undefined,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Something went wrong.";
      toast({
        variant: "destructive",
        title: "Couldn't create resource",
        description: message,
      });
    } finally {
      setBusy(null);
    }
  };

  const busyKind = busy as ChatResourceKind | null;

  const tip = (kind: ChatResourceKind, ready: string) => {
    if (busy && busy !== kind) return "Another export is in progress…";
    if (busy === kind) return "Creating…";
    return disabledReason ?? ready;
  };

  return (
    <ChatResourceFooter
      disabled={noSessionOrEmpty || isTranscriptLoading}
      busy={busyKind}
      onSavePdf={sessionId && !noSessionOrEmpty ? () => void run("pdf") : undefined}
      onVoiceNote={sessionId && !noSessionOrEmpty ? () => void run("voice_note") : undefined}
      tips={{
        pdf: tip("pdf", "A one-page printable / postable takeaway."),
        voice_note: tip("voice_note", "Hear a short spoken recap from this chat."),
      }}
    />
  );
};
